/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, exhaustMap, map, mapTo, switchMap, tap, takeUntil } from 'rxjs/operators';
import { concat, from, of } from 'rxjs';
import { storesClient } from '../api/stores/storesClient';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import {
    StoreDetailsActionType,
    fetchStoreCardDataErrorAction,
    StoreDetailsActions,
    fetchStoreDetailsSuccessAction,
    fetchStoreCashBoxesStatsSuccessAction,
    fetchStoreCashBoxesListSuccess,
    fetchStoreTurnoversByPeriodSuccessAction,
    fetchStoreTurnoversByPeriodErrorAction,
    fetchStoreTurnoversByCashBoxesSuccessAction,
    fetchStoreTurnoversByCashBoxesErrorAction,
    downloadTurnoversByPeriodSuccessAction,
    downloadTurnoversByPeriodErrorAction,
    downloadTurnoversByCashBoxesSuccessAction,
    downloadTurnoversByCashBoxesErrorAction,
    fetchCoordinatesHistorySuccessAction,
    fetchCoordinatesHistoryErrorAction,
    downloadCoordinatesHistorySuccessAction,
    downloadCoordinatesHistoryErrorAction,
    updateStoreDetailsSuccessAction,
    fetchStoreCashBoxesStatsErrorAction,
    fetchStorePhotosInfoSuccessAction,
    fetchStorePhotosInfoErrorAction,
    uploadStorePhotosSuccessAction,
    uploadStorePhotosErrorAction,
    deleteStorePhotosSuccessAction,
    setStorePhotoOnPreviewSuccessAction,
    setStorePhotoOnPreviewErrorAction,
    deleteStorePhotosErrorAction,
    fetchInspectionsHistorySuccessAction,
    fetchInspectionsHistoryErrorAction,
    createInspectionSuccessAction,
    createInspectionErrorAction,
    cancelInspectionSuccessAction,
    assignInspectionSuccessAction,
    assignInspectionErrorAction,
    completeInspectionSuccessAction,
    completeInspectionErrorAction,
    fetchStoreClassificationsHistorySuccessAction,
    fetchStoreClassificationsHistoryErrorAction,
    downloadStoreClassificationsHistorySuccessAction,
    downloadStoreClassificationsHistoryErrorAction,
    fetchStoreCashBoxesIntegrationDataSuccessAction,
    fetchStoreIntegrationsDataSuccessAction,
    fetchStoreIntegrationsDataErrorAction,
    applyStoreIntegrationsStatusChangesSuccessAction,
    changeStoreCashBoxesIntegrationStatusSuccess,
    storeCashBoxesIntegrationDataErrorAction,
} from '../actions/storeDetailsActions';
import { RootState } from '../store.typings';
import { SetStorePhotoOnPreviewDto } from '../api/stores/storesClientTypings';
import { cashboxesClient } from '../api/cashBoxes/cashboxesClient';

type StoreDetailsEpic = Epic<StoreDetailsActions, StoreDetailsActions, RootState>;

export const getStoreDetailsEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(
            StoreDetailsActionType.OPEN_REGISTRY_STORE_CARD_PAGE,
            StoreDetailsActionType.UPDATE_STORE_CASHBOXES_INTEGRATION_STATUS_SUCCESS,
            StoreDetailsActionType.UPDATE_STORE_INTEGRATION_STATUS_SUCCESS,
        ),
        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;

            return from(storesClient.getStore(StoreTaxNumber)).pipe(
                map((resp) => fetchStoreDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.storeWithTaxNumberNotFound', {
                                    searchText: StoreTaxNumber,
                                }),
                            ),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const updateStoreInfoEpic: StoreDetailsEpic = (action$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.UPDATE_STORE_DETAILS),
        switchMap((action) => {
            return from(storesClient.updateStoreInfo(action.payload)).pipe(
                map((resp) => updateStoreDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateStoreInfoFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const getStoreCashBoxesStats: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_STORE_CASHBOXES_STATS),
        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;

            return from(storesClient.getStoreCashBoxesStats(StoreTaxNumber)).pipe(
                map((resp) => {
                    return fetchStoreCashBoxesStatsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreCashBoxesStatsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashBoxesStatsNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const fetchCoordinatesHistoryEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_COORDINATES_HISTORY, StoreDetailsActionType.UPDATE_STORE_DETAILS_SUCCESS),
        switchMap(() => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const listName = i18n.t('RegistryScreens.coordinatesHistory');

            return from(storesClient.getCoordinatesHistory(storeTaxNumber)).pipe(
                map((resp) => {
                    return fetchCoordinatesHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCoordinatesHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const coordinatesHistoryDownloadEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.DOWNLOAD_COORDINATES_HISTORY),
        exhaustMap(() => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const fileName = `object${storeTaxNumber}_CoordinatesChangingHistory.xls`;

            return from(storesClient.downloadCoordinatesHistoryAsExcel(storeTaxNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadCoordinatesHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadCoordinatesHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchInspectionsHistoryEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_INSPECTIONS_HISTORY),
        switchMap(() => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const listName = i18n.t('inspection.inspectionsHistory');

            return from(storesClient.getInspectionsHistory(storeTaxNumber)).pipe(
                map((resp) => {
                    return fetchInspectionsHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchInspectionsHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const createInspectionEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.CREATE_INSPECTION),
        switchMap((action) => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;

            return from(storesClient.createInspection(storeTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return createInspectionSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(createInspectionErrorAction(parseError(err, i18n.t('inspection.errors.createInspectionError')))),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const cancelInspectionEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.CANCEL_INSPECTION),
        switchMap((action) => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const inspectionId = action.payload;

            return from(storesClient.cancelInspection(storeTaxNumber, inspectionId)).pipe(
                map((_resp) => {
                    return cancelInspectionSuccessAction(inspectionId);
                }),

                catchError((err: AxiosError) =>
                    of(createInspectionErrorAction(parseError(err, i18n.t('inspection.errors.cancelInspectionError')))),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const assignInspectionEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.ASSIGN_INSPECTION),
        switchMap((action) => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;

            return from(storesClient.assignInspection(storeTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return assignInspectionSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(assignInspectionErrorAction(parseError(err, i18n.t('inspection.errors.assignInspectionError')))),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const completeInspectionEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.COMPLETE_INSPECTION),
        switchMap((action) => {
            const storeTaxNumber = state$.value.routerState.route.params.StoreTaxNumber;

            return from(storesClient.completeInspection(storeTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return completeInspectionSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        completeInspectionErrorAction(
                            parseError(err, i18n.t('inspection.errors.completeInspectionError')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const getStoreCashboxesListEpic: Epic<StoreDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(
            StoreDetailsActionType.OPEN_REGISTRY_STORE_CARD_PAGE,
            StoreDetailsActionType.APPLY_CASHBOXES_FILTER,
            StoreDetailsActionType.APPLY_CASHBOXES_PAGING,
        ),
        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;

            const queryParams = {
                ...state$.value.storeDetailsReducer.cashBoxesPageParams,
                ...state$.value.storeDetailsReducer.cashBoxesFilterParams,
            };

            return concat(
                from(storesClient.getStoreCashboxes({ taxNumber: StoreTaxNumber, params: queryParams })).pipe(
                    map((resp) => {
                        return fetchStoreCashBoxesListSuccess(resp);
                    }),

                    catchError((err: AxiosError) =>
                        of(
                            fetchStoreCardDataErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.cashBoxesRequestFailed')),
                            ),
                        ),
                    ),

                    takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
                ),
            );
        }),
    );
};

export const getStoreTurnoversByPeriodEpic: Epic<StoreDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_PERIOD),

        switchMap((action) => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;

            return from(storesClient.getStoreTurnoversByPeriod(StoreTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return fetchStoreTurnoversByPeriodSuccessAction({ turnovers: resp, params: action.payload });
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreTurnoversByPeriodErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.turnoversRequestFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const getStoreTurnoversByCashBoxesEpic: Epic<StoreDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(
            StoreDetailsActionType.FETCH_STORE_TURNOVERS_BY_CASHBOXES,
            StoreDetailsActionType.APPLY_TURNOVERS_BY_CASHBOXES_PAGING,
            StoreDetailsActionType.APPLY_TURNOVERS_BY_CASHBOXES_FILTER,
        ),

        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;
            const queryParams = {
                ...state$.value.storeDetailsReducer.turnoversByCashBoxesPageParams,
                ...state$.value.storeDetailsReducer.turnoversByCashBoxesFilterParams,
            };

            return from(storesClient.getStoreTurnoversByCashBoxes(StoreTaxNumber, queryParams)).pipe(
                map((resp) => {
                    return fetchStoreTurnoversByCashBoxesSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreTurnoversByCashBoxesErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.turnoversRequestFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const storeTurnoversByPeriodDownloadEpic: Epic<StoreDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD),
        exhaustMap((action) => {
            const params = action.payload;
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const fileName = `object_${taxNumber}_TurnoversByPeriod.xlsx`;

            return from(storesClient.downloadStoreTurnoversByPeriodAsExcel(taxNumber, params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadTurnoversByPeriodSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadTurnoversByPeriodErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const storeTurnoversByCashBoxesDownloadEpic: Epic<StoreDetailsActions> = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.DOWNLOAD_TURNOVERS_BY_CASHBOXES),
        exhaustMap(() => {
            const params = { ...state$.value.storeDetailsReducer.turnoversByCashBoxesFilterParams };
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const fileName = `object_${taxNumber}_TurnoversByCashboxes.xlsx`;

            return from(storesClient.downloadStoreTurnoversByCashBoxesAsExcel(taxNumber, params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadTurnoversByCashBoxesSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadTurnoversByCashBoxesErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const getStorePhotosInfoEpic: StoreDetailsEpic = (action$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_STORE_PHOTOS_INFO),
        switchMap((action) => {
            return from(storesClient.getStorePhotosInfo(action.payload.storeTaxNumber, action.payload.bucketName)).pipe(
                map((resp) => fetchStorePhotosInfoSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchStorePhotosInfoErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.getStorePhotosFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const uploadPhotosEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.UPLOAD_STORE_PHOTOS),
        switchMap((action) => {
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const filesToUpload: SetStorePhotoOnPreviewDto[] = action.payload.params.map((file) => {
                return {
                    ...file,
                    name: file.name,
                    submitted: true,
                    bucketName: action.payload.bucketName,
                };
            });

            return from(storesClient.setPreviewOrSubmit(taxNumber, filesToUpload)).pipe(
                map(() => uploadStorePhotosSuccessAction(action.payload.params)),

                catchError((err: AxiosError) =>
                    of(uploadStorePhotosErrorAction(parseError(err, i18n.t('errors.failedUploadFile')))),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const setOnPreviewEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.SET_STORE_PHOTO_ON_PREVIEW),
        switchMap((action) => {
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;

            return from(storesClient.setPreviewOrSubmit(taxNumber, action.payload)).pipe(
                map(() => setStorePhotoOnPreviewSuccessAction(action.payload[0].name)),

                catchError((err: AxiosError) =>
                    of(
                        setStorePhotoOnPreviewErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateStoreInfoFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const deleteStorePhotosEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.DELETE_STORE_PHOTOS),
        switchMap((action) => {
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;

            return from(
                storesClient.deleteStorePhoto(
                    taxNumber,
                    [action.payload.name],
                    action.payload.bucketName,
                    action.payload.addedPhotoUserId,
                ),
            ).pipe(
                map(() => deleteStorePhotosSuccessAction(action.payload.name)),

                catchError((err: AxiosError) =>
                    of(deleteStorePhotosErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const fetchStoreClassificationsHistoryEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_START),
        switchMap((action) => {
            const params = action.payload;
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const listName = i18n.t('RegistryScreens.aiClassificationsHistory').toLocaleLowerCase();

            return from(storesClient.getAiClassificationsHistory(taxNumber, params)).pipe(
                map((resp) => {
                    return fetchStoreClassificationsHistorySuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreClassificationsHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedLoadList', { listName })),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const storeClassificationsHistoryDownloadEpic: Epic<StoreDetailsActions, StoreDetailsActions, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_START),
        exhaustMap(() => {
            const taxNumber = state$.value.routerState.route.params.StoreTaxNumber;
            const fileName = `object_${taxNumber}_AiClassificationsHistory.xls`;

            return from(storesClient.downloadAiClassificationsHistoryAsExcel(taxNumber)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadStoreClassificationsHistorySuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadStoreClassificationsHistoryErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const storeCashBoxesIntegrationEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(
            StoreDetailsActionType.FETCH_STORE_CASHBOXES_INTEGRATION_DATA_START,
            StoreDetailsActionType.APPLY_STORE_CASHBOXES_INTEGRATION_PAGING,
            StoreDetailsActionType.UPDATE_STORE_CASHBOXES_INTEGRATION_STATUS_SUCCESS,
            StoreDetailsActionType.UPDATE_STORE_INTEGRATION_STATUS_SUCCESS,
        ),
        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;
            const params = {
                ...state$.value.storeDetailsReducer.storeCashBoxesIntegrationPageParams,
            };
            return from(storesClient.getStoreCashBoxesIntegrationList(StoreTaxNumber, params)).pipe(
                map((resp) => {
                    return fetchStoreCashBoxesIntegrationDataSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        storeCashBoxesIntegrationDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedToGetCashBoxIntegrationData')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const storeIntegrationStatusEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.FETCH_STORE_INTEGRATION_DATA_START),
        switchMap(() => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;
            return from(storesClient.getStoreIntegrationStatus(StoreTaxNumber)).pipe(
                map((resp) => {
                    return fetchStoreIntegrationsDataSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreIntegrationsDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedToGetStoreIntegrationData')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const changeStoreIntegrationStatusEpic: StoreDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.UPDATE_STORE_INTEGRATION_STATUS_START),
        switchMap((action) => {
            const { StoreTaxNumber } = state$.value.routerState.route.params;
            return from(storesClient.changeStoreIntegrationStatus(StoreTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return applyStoreIntegrationsStatusChangesSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchStoreIntegrationsDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateStoreIntegrationFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};

export const changeStoreCashBoxesIntegrationStatusEpic: StoreDetailsEpic = (action$) => {
    return action$.pipe(
        ofType(StoreDetailsActionType.UPDATE_STORE_CASHBOXES_INTEGRATION_STATUS),
        switchMap((action) => {
            return from(cashboxesClient.changeCashBoxIntegrationStatus(action.payload)).pipe(
                map(() => {
                    return changeStoreCashBoxesIntegrationStatusSuccess();
                }),

                catchError((err: AxiosError) =>
                    of(
                        storeCashBoxesIntegrationDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateCashBoxIntegrationFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(StoreDetailsActionType.EXIT_REGISTRY_STORE_CARD_PAGE)),
            );
        }),
    );
};
